"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/components/ui/alert-dialog";
import { Button } from "@shared/components/ui/button";
import { Command, CommandGroup, CommandItem, CommandList } from "@shared/components/ui/command";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { toast } from "@shared/components/ui/snackbar";
import { Table, TableBody, TableCell, TableRow } from "@shared/components/ui/table";
import { formatLocalDateTime } from "@shared/lib/formatters";
import { IconExternalLink } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AdminUsersDangerZone } from "@/components/admin.users.danger-zone";
import type { AdminProjectSummary } from "@/lib/admin/directory.hooks";
import {
  useAdminCompanyNameSuggestions,
  useAdminRestoreUser,
  useAdminSoftDeleteUser,
  useAdminSyncWorkosUserId,
  useAdminUpdateUser,
  useAdminUser,
} from "@/lib/admin/users.hooks";
import { adminPath } from "@/lib/auth";
import { graphqlErrorMessage } from "@/lib/graphql/client";
import { useStripeConfig } from "@/lib/useStripeConfig";
import { cn } from "@/lib/utils";

const nativeSelectClass = cn(
  "border-input dark:bg-input/30 flex h-9 w-full min-w-0 appearance-none rounded-md border bg-transparent py-1 pl-3 pr-8 text-sm shadow-xs transition-[color,box-shadow] outline-none",
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
);

const StripeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
  </svg>
);

const HubSpotIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M18.164 7.93V5.82a1.56 1.56 0 0 0 .898-1.408V4.36a1.56 1.56 0 0 0-1.56-1.56h-.043a1.56 1.56 0 0 0-1.56 1.56v.054a1.56 1.56 0 0 0 .898 1.406v2.12a4.42 4.42 0 0 0-2.1.924L8.76 4.745a1.745 1.745 0 1 0-1.03.894L14.1 9.826a4.43 4.43 0 0 0-.617 2.27 4.43 4.43 0 0 0 .614 2.267l-1.895 1.384a1.4 1.4 0 1 0 .898 1.188 1.4 1.4 0 0 0-.082-.472l1.918-1.4a4.46 4.46 0 0 0 2.735.934 4.46 4.46 0 0 0 4.46-4.46 4.46 4.46 0 0 0-3.966-4.607zm-.708 7.184a2.577 2.577 0 1 1 2.577-2.577 2.577 2.577 0 0 1-2.577 2.577z" />
  </svg>
);

interface AdminUsersOverviewTabProps {
  userId: string;
  onDeleted: () => void;
  hsPortalId?: string | null;
  projects?: AdminProjectSummary[];
}

export const AdminUsersOverviewTab = ({ userId, onDeleted, hsPortalId, projects = [] }: AdminUsersOverviewTabProps) => {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useAdminUser(userId);
  const updateUser = useAdminUpdateUser();
  const softDelete = useAdminSoftDeleteUser();
  const restoreUser = useAdminRestoreUser();
  const syncWorkosUserId = useAdminSyncWorkosUserId();
  const companySuggestionsQuery = useAdminCompanyNameSuggestions();
  const { isTestMode: stripeIsTestMode } = useStripeConfig();

  const user = data?.adminUser;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accountStatus, setAccountStatus] = useState<"active" | "deactivated">("active");
  const [jobtitle, setJobtitle] = useState("");
  const [company, setCompany] = useState("");
  const [companyOpen, setCompanyOpen] = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name ?? "");
    setLastName(user.last_name ?? "");
    setJobtitle(user.jobtitle ?? "");
    setCompany(user.company ?? "");
    setAccountStatus(user.status === "active" ? "active" : "deactivated");
  }, [user]);

  // Company autocomplete
  const allCompanyNames = companySuggestionsQuery.data?.adminCompanyNameSuggestions ?? [];
  const filteredCompanyNames = useMemo(() => {
    if (!company.trim()) return allCompanyNames.slice(0, 8);
    const q = company.toLowerCase();
    return allCompanyNames.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
  }, [allCompanyNames, company]);

  useEffect(() => {
    if (!companyOpen) return;
    const handler = (e: MouseEvent) => {
      if (companyRef.current && !companyRef.current.contains(e.target as Node)) {
        setCompanyOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [companyOpen]);

  if (isLoading || !user) {
    return <p className="py-4 text-sm text-muted-foreground">Loading…</p>;
  }

  const isDeleted = Boolean(user.deleted_at);
  const fieldId = (name: string) => `user-overview-${userId}-${name}`;

  const hsContactUrl =
    hsPortalId && user.hubspot_contact_id
      ? `https://app.hubspot.com/contacts/${hsPortalId}/record/0-1/${user.hubspot_contact_id}`
      : hsPortalId && user.email
        ? `https://app.hubspot.com/contacts/${hsPortalId}/objects/0-1/views/all/list?query=${encodeURIComponent(user.email)}`
        : null;

  const profilePayload = () => ({
    id: userId,
    first_name: firstName.trim() || null,
    last_name: lastName.trim() || null,
    jobtitle: jobtitle.trim() || null,
    company: company.trim() || null,
  });

  const hasProfileChanges =
    (firstName.trim() || null) !== (user.first_name ?? null) ||
    (lastName.trim() || null) !== (user.last_name ?? null) ||
    (jobtitle.trim() || null) !== (user.jobtitle ?? null) ||
    (company.trim() || null) !== (user.company ?? null);

  const invalidateAndRefetch = () => {
    void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
    void refetch();
  };

  const handleSoftDelete = () => {
    softDelete.mutate(
      { id: userId },
      {
        onSuccess: () => {
          toast.success("User deactivated.");
          setDeactivateDialogOpen(false);
          invalidateAndRefetch();
        },
        onError: () => toast.error("Failed to deactivate user."),
      },
    );
  };

  const handleConfirmDeactivate = () => {
    // Run soft-delete only. If there are unsaved profile changes, discard them;
    // combining both mutations risks a partial-failure state where the profile
    // saves but the user remains active when the soft-delete then fails.
    handleSoftDelete();
  };

  const handleAccountStatusChange = (value: "active" | "deactivated") => {
    if (value === "deactivated") {
      setAccountStatus("deactivated");
      setDeactivateDialogOpen(true);
      return;
    }

    setAccountStatus("active");
  };

  const handleDeactivateDialogOpenChange = (open: boolean) => {
    setDeactivateDialogOpen(open);
    if (!open) {
      setAccountStatus("active");
    }
  };

  const handleSave = () => {
    if (!hasProfileChanges) {
      toast.message("No changes to save.");
      return;
    }

    updateUser.mutate(profilePayload(), {
      onSuccess: () => {
        toast.success("User updated.");
        invalidateAndRefetch();
      },
      onError: () => toast.error("Failed to update user."),
    });
  };

  const handleRestore = () => {
    restoreUser.mutate(
      { id: userId },
      {
        onSuccess: () => {
          toast.success("User restored.");
          void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
          void refetch();
        },
        onError: () => toast.error("Failed to restore user."),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {!isDeleted && (
        <div className="grid gap-4 rounded border bg-muted/20 p-4">
          <p className="text-sm font-medium">Profile</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor={fieldId("first-name")}>First name</Label>
              <Input id={fieldId("first-name")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={fieldId("last-name")}>Last name</Label>
              <Input id={fieldId("last-name")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor={fieldId("company")}>Company name</Label>
              <div ref={companyRef} className="relative">
                <Input
                  id={fieldId("company")}
                  value={company}
                  autoComplete="off"
                  onChange={(e) => {
                    setCompany(e.target.value);
                    setCompanyOpen(true);
                  }}
                  onFocus={() => setCompanyOpen(true)}
                />
                {companyOpen && filteredCompanyNames.length > 0 && (
                  <div className="bg-popover absolute top-full left-0 right-0 z-50 mt-1 overflow-hidden rounded-md border shadow-md">
                    <Command shouldFilter={false}>
                      <CommandList>
                        <CommandGroup>
                          {filteredCompanyNames.map((name) => (
                            <CommandItem
                              key={name}
                              value={name}
                              onSelect={() => {
                                setCompany(name);
                                setCompanyOpen(false);
                              }}
                              className={cn(company === name && "bg-accent")}
                            >
                              {name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor={fieldId("jobtitle")}>Job title</Label>
              <Input id={fieldId("jobtitle")} value={jobtitle} onChange={(e) => setJobtitle(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor={fieldId("status")}>Status</Label>
            <div className="relative">
              <select
                id={fieldId("status")}
                value={accountStatus}
                onChange={(e) => handleAccountStatusChange(e.target.value as "active" | "deactivated")}
                className={nativeSelectClass}
              >
                <option value="active">Active</option>
                <option value="deactivated">Deactivated</option>
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground opacity-50" />
            </div>
          </div>

          <Button type="button" onClick={handleSave} disabled={updateUser.isPending || softDelete.isPending}>
            Save changes
          </Button>
        </div>
      )}

      <AlertDialog open={deactivateDialogOpen} onOpenChange={handleDeactivateDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate user?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the user locally and removes them from the active user list. The account can be restored
              later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateUser.isPending || softDelete.isPending}
              onClick={handleConfirmDeactivate}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isDeleted && (
        <div className="rounded border border-dashed bg-muted/20 p-4">
          <p className="mb-3 text-sm text-muted-foreground">
            This user is deactivated. Restore them to edit profile fields.
          </p>
          <Button type="button" variant="outline" onClick={handleRestore} disabled={restoreUser.isPending}>
            Restore user
          </Button>
        </div>
      )}

      <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 rounded border bg-muted/20 p-4 text-sm">
        {(
          [
            ["User ID", user.id],
            ["Email", user.email],
            ["Timezone", user.timezone ?? "—"],
            ["Last logged in", user.last_login_at ? formatLocalDateTime(user.last_login_at) : "—"],
            ["Created", formatLocalDateTime(user.created_at)],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="contents">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium break-all">{value}</span>
          </div>
        ))}
        <div className="contents">
          <span className="text-muted-foreground">Org</span>
          <span className="font-medium break-all">
            {user.org_id ? (
              <a href={adminPath(`/orgs/${user.org_id}`)} className="underline-offset-4 hover:underline">
                {user.org_name || user.org_id}
              </a>
            ) : (
              (user.org_name ?? "—")
            )}
          </span>
        </div>
        {/* WorkOS user ID — shown separately so we can attach a Sync action */}
        <div className="contents">
          <span className="text-muted-foreground">WorkOS user ID</span>
          <span className="flex items-center gap-2 break-all font-medium">
            {user.workos_user_id ?? (
              <span className="text-yellow-600 dark:text-yellow-400">
                Missing — user has not signed up yet or ID is stale
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                syncWorkosUserId.mutate(
                  { id: user.id },
                  {
                    onSuccess: (data) => {
                      const newId = data.adminSyncWorkosUserId.workos_user_id;
                      toast.success(newId ? `WorkOS ID synced: ${newId}` : "No WorkOS account found for this email.");
                      void queryClient.invalidateQueries({ queryKey: ["adminUser", userId] });
                    },
                    onError: (error) => toast.error(graphqlErrorMessage(error, "Failed to sync WorkOS user ID.")),
                  },
                );
              }}
              disabled={syncWorkosUserId.isPending}
              className="shrink-0 rounded border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              title="Look up this user in WorkOS by email and update the stored ID"
            >
              {syncWorkosUserId.isPending ? "Syncing…" : "Sync"}
            </button>
          </span>
        </div>
        {user.stripe_customer_id && (
          <>
            <span className="text-muted-foreground">Stripe</span>
            <span className="inline-flex items-center gap-2">
              <span className="font-medium">{user.stripe_customer_id}</span>
              <a
                href={
                  stripeIsTestMode
                    ? `https://dashboard.stripe.com/test/customers/${user.stripe_customer_id}`
                    : `https://dashboard.stripe.com/customers/${user.stripe_customer_id}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-foreground underline-offset-4 hover:underline"
              >
                <StripeIcon className="h-3.5 w-3.5 text-[#635BFF]" />
                Open
                <IconExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            </span>
          </>
        )}
        {hsContactUrl && (
          <>
            <span className="text-muted-foreground">HubSpot</span>
            <span className="inline-flex items-center gap-2">
              {user.hubspot_contact_id && <span className="font-medium">{user.hubspot_contact_id}</span>}
              <a
                href={hsContactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-foreground underline-offset-4 hover:underline"
              >
                <HubSpotIcon className="h-4 w-4 text-[#FF7A59]" />
                Open
                <IconExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            </span>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Projects</p>
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No projects.</p>
        ) : (
          <div className="rounded border">
            <Table>
              <TableBody>
                {projects.map((proj) => (
                  <TableRow key={proj.id}>
                    <TableCell className="text-table font-medium">{proj.name || "Untitled project"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AdminUsersDangerZone userId={userId} email={user.email} onDeleted={onDeleted} />
    </div>
  );
};
